import { z } from "zod";
import { colorSchema, dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { clientRefSchema } from "./clients.ts";
import { blankToNull, csvArraySchema, hhmmSchema, queryBooleanSchema, timestampsShape, userRefSchema, weekdaySchema } from "./common.ts";
import { projectRefSchema } from "./projects.ts";

/**
 * CONTRACT of the `tasks` module — PRD §7 Item 3 (the task half of delivery):
 * US-3.2 task management, US-3.3 views (list / board / calendar / **My Work**),
 * US-3.4 recurring tasks and US-3.7 bulk operations.
 *
 * Three rules run through every shape below.
 *
 *  1. **A status is a workspace record, a category is a contract.** Agencies
 *     rename "In review" to "With client" all the time, so the status is
 *     configurable per org while the five CATEGORIES are fixed — every filter,
 *     every roll-up and every "is this done?" question is asked of the category,
 *     never of the name.
 *  2. **A task hangs off a project, and a project hangs off a client.** A task
 *     may be standalone (personal to-do, `projectId: null`), but it never points
 *     at a lead, and `client` is a read-only echo of the project's client.
 *  3. **Subtasks are one level deep.** `parentTaskId` may only name a top-level
 *     task, which is what keeps every board, every roll-up and every bulk
 *     operation a flat, bounded thing rather than a tree walk.
 *
 * Optimistic concurrency: `taskSchema.version` comes back on every read and may
 * be sent on PATCH; a stale one answers 409 with `{ currentVersion, sentVersion }`.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The five fixed categories every workspace status maps to (US-3.2). Renaming a
 * status never changes what the product believes about progress.
 */
export const TASK_STATUS_CATEGORIES = ["not_started", "in_progress", "in_review", "done", "cancelled"] as const;
export type TaskStatusCategory = (typeof TASK_STATUS_CATEGORIES)[number];
export const taskStatusCategorySchema = z.enum(TASK_STATUS_CATEGORIES);

/** Categories that still need somebody's attention — "open" everywhere in the product. */
export const OPEN_TASK_CATEGORIES = ["not_started", "in_progress", "in_review"] as const satisfies readonly TaskStatusCategory[];
export type OpenTaskCategory = (typeof OPEN_TASK_CATEGORIES)[number];

export function isOpenTaskCategory(category: string): boolean {
  return (OPEN_TASK_CATEGORIES as readonly string[]).includes(category);
}

export const TASK_PRIORITIES = ["urgent", "high", "normal", "low"] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

/** Sort weight for the priority chip — urgent first, low last. */
export const TASK_PRIORITY_ORDER: Record<TaskPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

/** Phase 1 ships `blocks` only; the schema carries the type so Gantt (Phase 2) needs no migration. */
export const TASK_DEPENDENCY_TYPES = ["blocks"] as const;
export type TaskDependencyType = (typeof TASK_DEPENDENCY_TYPES)[number];
export const taskDependencyTypeSchema = z.enum(TASK_DEPENDENCY_TYPES);

/** US-3.4 rules. `every_n` carries its own unit; the other three are self-describing. */
export const RECURRENCE_FREQUENCIES = ["daily", "weekly", "monthly", "every_n"] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];
export const recurrenceFrequencySchema = z.enum(RECURRENCE_FREQUENCIES);

export const RECURRENCE_UNITS = ["day", "week", "month"] as const;
export type RecurrenceUnit = (typeof RECURRENCE_UNITS)[number];
export const recurrenceUnitSchema = z.enum(RECURRENCE_UNITS);

/** Generate on the calendar, or only once the previous instance is finished (US-3.4). */
export const RECURRENCE_GENERATION_MODES = ["on_schedule", "on_completion"] as const;
export type RecurrenceGenerationMode = (typeof RECURRENCE_GENERATION_MODES)[number];
export const recurrenceGenerationModeSchema = z.enum(RECURRENCE_GENERATION_MODES);

/** The four buckets of **My Work** (US-3.3), in the order the screen renders them. */
export const MY_WORK_BUCKETS = ["overdue", "today", "this_week", "later"] as const;
export type MyWorkBucket = (typeof MY_WORK_BUCKETS)[number];
export const myWorkBucketSchema = z.enum(MY_WORK_BUCKETS);

/** How the list view may be grouped (US-3.3). */
export const TASK_GROUP_BY = ["status", "assignee", "priority", "due_date", "project"] as const;
export type TaskGroupBy = (typeof TASK_GROUP_BY)[number];
export const taskGroupBySchema = z.enum(TASK_GROUP_BY);

/** `T-0001`, gapless per workspace. */
export const TASK_NUMBER_REGEX = /^T-\d{4,}$/;

/** Soft-deleted tasks stay restorable for 30 days, then the purge job removes them (US-3.7). */
export const TASK_TRASH_RETENTION_DAYS = 30;

/** The statuses seeded into a new workspace, in board order. */
export const DEFAULT_TASK_STATUSES = [
  { name: "To do", category: "not_started", color: "#94a3b8", isDefault: true },
  { name: "In progress", category: "in_progress", color: "#3b82f6", isDefault: false },
  { name: "In review", category: "in_review", color: "#a855f7", isDefault: false },
  { name: "Done", category: "done", color: "#22c55e", isDefault: false },
  { name: "Cancelled", category: "cancelled", color: "#ef4444", isDefault: false },
] as const satisfies readonly { name: string; category: TaskStatusCategory; color: string; isDefault: boolean }[];

// ---------------------------------------------------------------------------
// Response DTOs — no `.transform()`, no reliance on `.default()`, every key sent
// ---------------------------------------------------------------------------

/** A workspace-configurable task status (US-3.2). */
export const taskStatusSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  category: taskStatusCategorySchema,
  color: z.string().nullable(),
  position: z.number().int(),
  /** The status a new task lands in when the caller does not name one. */
  isDefault: z.boolean(),
  /** Seeded at signup: it may be renamed and recoloured, but not deleted. */
  isSystem: z.boolean(),
  ...timestampsShape,
});
export type TaskStatusDto = z.infer<typeof taskStatusSchema>;

/** Compact status reference embedded in task payloads and board columns. */
export const taskStatusRefSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  category: taskStatusCategorySchema,
  color: z.string().nullable(),
});
export type TaskStatusRef = z.infer<typeof taskStatusRefSchema>;

/** One line of a task's checklist. `id` is stable so the UI can toggle a single row. */
export const checklistItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  done: z.boolean(),
  doneAt: timestampSchema.nullable(),
  doneByUserId: uuidSchema.nullable(),
});
export type ChecklistItemDto = z.infer<typeof checklistItemSchema>;

/** Compact task reference other modules and the dependency lists embed. */
export const taskRefSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  title: z.string(),
  statusCategory: taskStatusCategorySchema,
  dueDate: dateOnlySchema.nullable(),
});
export type TaskRef = z.infer<typeof taskRefSchema>;

/** A task (US-3.2). `client` echoes the project's client and is never set directly. */
export const taskSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  title: z.string(),
  /** Markdown. */
  description: z.string().nullable(),
  projectId: uuidSchema.nullable(),
  project: projectRefSchema.nullable(),
  clientId: uuidSchema.nullable(),
  client: clientRefSchema.nullable(),
  parentTaskId: uuidSchema.nullable(),
  parent: taskRefSchema.nullable(),
  statusId: uuidSchema,
  status: taskStatusRefSchema,
  statusCategory: taskStatusCategorySchema,
  assigneeId: uuidSchema.nullable(),
  assignee: userRefSchema.nullable(),
  /** User ids following the task; they are notified on comments and completion. */
  watcherIds: z.array(uuidSchema),
  watchers: z.array(userRefSchema),
  startDate: dateOnlySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  /** Optional wall-clock time on the due date, "HH:mm" in the workspace timezone. */
  dueTime: z.string().nullable(),
  /** `dueDate` + `dueTime` resolved to an instant — what My Work and the overdue sweep read. */
  dueAt: timestampSchema.nullable(),
  priority: taskPrioritySchema,
  estimateMinutes: z.number().int().nullable(),
  /** Counts toward the engagement's retainer quota when the task is completed (US-3.5). */
  deliverableType: z.string().nullable(),
  checklist: z.array(checklistItemSchema),
  position: z.number().int(),
  /** Set when the task was generated by a recurring rule (US-3.4). */
  recurringRuleId: uuidSchema.nullable(),
  /** The period key that made the generation idempotent, e.g. `2026-09-21` or `2026-09`. */
  recurringPeriod: z.string().nullable(),
  isOverdue: z.boolean(),
  /** Open subtask / total subtask counts — the detail view lists them in full. */
  subtaskTotal: z.number().int(),
  subtaskDone: z.number().int(),
  /** True while at least one predecessor is still open. */
  isBlocked: z.boolean(),
  completedAt: timestampSchema.nullable(),
  completedBy: userRefSchema.nullable(),
  createdBy: userRefSchema.nullable(),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  /** Optimistic concurrency token — send it back on PATCH to detect a lost update. */
  version: z.number().int(),
  deletedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type TaskDto = z.infer<typeof taskSchema>;

/** `predecessor` blocks `successor` — "successor is blocked by predecessor". */
export const taskDependencySchema = z.object({
  id: uuidSchema,
  type: taskDependencyTypeSchema,
  predecessorTaskId: uuidSchema,
  predecessor: taskRefSchema.nullable(),
  successorTaskId: uuidSchema,
  successor: taskRefSchema.nullable(),
  createdAt: timestampSchema,
});
export type TaskDependencyDto = z.infer<typeof taskDependencySchema>;

/** GET /api/v1/tasks/:id — the task plus everything the detail drawer draws. */
export const taskDetailSchema = taskSchema.extend({
  subtasks: z.array(taskSchema),
  /** Tasks this one blocks. */
  blocks: z.array(taskDependencySchema),
  /** Tasks that block this one. */
  blockedBy: z.array(taskDependencySchema),
});
export type TaskDetailDto = z.infer<typeof taskDetailSchema>;

/** One bucket of My Work, already ordered by due date then priority. */
export const myWorkBucketGroupSchema = z.object({
  bucket: myWorkBucketSchema,
  total: z.number().int(),
  items: z.array(taskSchema),
});
export type MyWorkBucketGroup = z.infer<typeof myWorkBucketGroupSchema>;

/**
 * GET /api/v1/tasks/my-work — Overdue / Today / This week / Later across every
 * project, the most-used screen in the product. One indexed query feeds it.
 */
export const myWorkSchema = z.object({
  /** Whose work this is (an admin may ask for a colleague's). */
  userId: uuidSchema,
  /** "YYYY-MM-DD" in the workspace timezone — the day the buckets were cut on. */
  today: dateOnlySchema,
  buckets: z.array(myWorkBucketGroupSchema),
  /** The nav badge: how many of this person's open tasks are past due. */
  overdueCount: z.number().int(),
  /** Every open task assigned to this person, however far out. */
  openCount: z.number().int(),
  /** True when the buckets were capped by `limit` and more work exists. */
  truncated: z.boolean(),
});
export type MyWorkDto = z.infer<typeof myWorkSchema>;

/** One column of the board view — a status and the tasks sitting in it. */
export const taskBoardColumnSchema = z.object({
  status: taskStatusRefSchema,
  total: z.number().int(),
  items: z.array(taskSchema),
});
export type TaskBoardColumn = z.infer<typeof taskBoardColumnSchema>;

/** GET /api/v1/tasks/board — the Kanban view, every status as a column (US-3.3). */
export const taskBoardSchema = z.object({ columns: z.array(taskBoardColumnSchema) });
export type TaskBoardDto = z.infer<typeof taskBoardSchema>;

/** One day of the calendar view. */
export const taskCalendarDaySchema = z.object({ date: dateOnlySchema, items: z.array(taskSchema) });
export type TaskCalendarDay = z.infer<typeof taskCalendarDaySchema>;

/** GET /api/v1/tasks/calendar — tasks by due date across a window (US-3.3). */
export const taskCalendarSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  days: z.array(taskCalendarDaySchema),
  /** Tasks in range with no due date are never invented onto a day; they come back here. */
  undated: z.array(taskSchema),
});
export type TaskCalendarDto = z.infer<typeof taskCalendarSchema>;

/** One group of the grouped list view. `key` is null for "no assignee" / "no due date". */
export const taskGroupSchema = z.object({
  key: z.string().nullable(),
  label: z.string(),
  total: z.number().int(),
  items: z.array(taskSchema),
});
export type TaskGroupDto = z.infer<typeof taskGroupSchema>;

/** GET /api/v1/tasks/grouped — the list view, grouped by status / assignee / … (US-3.3). */
export const taskGroupListSchema = z.object({ groupBy: taskGroupBySchema, groups: z.array(taskGroupSchema) });
export type TaskGroupListDto = z.infer<typeof taskGroupListSchema>;

/** What a bulk operation actually did (US-3.7). */
export const bulkTaskResultSchema = z.object({
  /** Ids the caller may see AND that changed. */
  updated: z.array(uuidSchema),
  /** Ids that were asked for but skipped (not visible, already in that state, or invalid). */
  skipped: z.array(uuidSchema),
  updatedCount: z.number().int(),
  skippedCount: z.number().int(),
});
export type BulkTaskResultDto = z.infer<typeof bulkTaskResultSchema>;

/** A non-working day the recurring generator steps over (US-3.4). */
export const workHolidaySchema = z.object({
  id: uuidSchema,
  date: dateOnlySchema,
  name: z.string(),
  ...timestampsShape,
});
export type WorkHolidayDto = z.infer<typeof workHolidaySchema>;

/** The shape a recurring rule stamps onto every instance it creates. */
export const taskTemplatePayloadSchema = z.object({
  title: z.string(),
  description: z.string().nullable(),
  assigneeId: uuidSchema.nullable(),
  statusId: uuidSchema.nullable(),
  priority: taskPrioritySchema,
  estimateMinutes: z.number().int().nullable(),
  deliverableType: z.string().nullable(),
  checklist: z.array(z.string()),
  watcherIds: z.array(uuidSchema),
  tags: z.array(z.string()),
  /** Days between the occurrence date and the instance's due date (0 = due that day). */
  dueOffsetDays: z.number().int(),
});
export type TaskTemplatePayload = z.infer<typeof taskTemplatePayloadSchema>;

/** How often a rule fires, and on which days (US-3.4). */
export const recurrenceConfigSchema = z.object({
  /** `every_n` uses this with `unit`; `weekly`/`monthly` use it as "every N weeks/months". */
  interval: z.number().int(),
  unit: recurrenceUnitSchema,
  /** Weekly rules: 0 = Sunday … 6 = Saturday. Empty for the other frequencies. */
  weekdays: z.array(z.number().int()),
  /** Monthly rules: the day of the month, or null when `lastWorkingDay` is set. */
  dayOfMonth: z.number().int().nullable(),
  /** Monthly rules: fire on the last WORKING day of the month instead of a fixed date. */
  lastWorkingDay: z.boolean(),
});
export type RecurrenceConfig = z.infer<typeof recurrenceConfigSchema>;

/** A recurring task rule (US-3.4). */
export const recurringTaskRuleSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  projectId: uuidSchema.nullable(),
  project: projectRefSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  frequency: recurrenceFrequencySchema,
  config: recurrenceConfigSchema,
  generationMode: recurrenceGenerationModeSchema,
  template: taskTemplatePayloadSchema,
  startDate: dateOnlySchema,
  /** Generation also stops at the engagement's end date, whichever comes first. */
  endDate: dateOnlySchema.nullable(),
  nextRunAt: timestampSchema.nullable(),
  /** The last period key generated — the other half of the idempotency guarantee. */
  lastGeneratedPeriod: z.string().nullable(),
  lastGeneratedAt: timestampSchema.nullable(),
  isActive: z.boolean(),
  /** Why the generator is currently not producing instances, in plain words. */
  pausedReason: z.string().nullable(),
  createdBy: userRefSchema.nullable(),
  ...timestampsShape,
});
export type RecurringTaskRuleDto = z.infer<typeof recurringTaskRuleSchema>;

/** One occurrence the rule would produce — the preview the PM confirms against. */
export const recurrenceOccurrenceSchema = z.object({
  /** The idempotency key half: one instance per rule per period, ever. */
  period: z.string(),
  /** The calendar date the rule fired on, before any holiday shift. */
  scheduledDate: dateOnlySchema,
  /** Where the instance actually lands after skipping weekends and holidays. */
  dueDate: dateOnlySchema,
  shifted: z.boolean(),
  /** "Diwali", "Sunday" — why it moved, or null when it did not. */
  shiftReason: z.string().nullable(),
  /** True when an instance for that period already exists. */
  alreadyGenerated: z.boolean(),
});
export type RecurrenceOccurrenceDto = z.infer<typeof recurrenceOccurrenceSchema>;

/** GET /api/v1/recurring-task-rules/:id/preview → the next N occurrences. */
export const recurrencePreviewSchema = z.object({
  ruleId: uuidSchema,
  occurrences: z.array(recurrenceOccurrenceSchema),
  /** Null while the rule would keep generating; a sentence when something stops it. */
  stoppedReason: z.string().nullable(),
});
export type RecurrencePreviewDto = z.infer<typeof recurrencePreviewSchema>;

// ---------------------------------------------------------------------------
// Inputs — parsed, so `.strict()`, `.trim()`, `.default()` and transforms are fine
// ---------------------------------------------------------------------------

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const taskStatusWritableShape = {
  name: z.string().trim().min(1).max(60),
  category: taskStatusCategorySchema,
  color: colorSchema.nullable(),
  position: z.number().int().min(0),
  isDefault: z.boolean(),
};

/** POST /api/v1/task-statuses (perm `projects:write`) → 201 TaskStatus. Duplicate name → CONFLICT. */
export const createTaskStatusInputSchema = z
  .object({
    ...taskStatusWritableShape,
    color: taskStatusWritableShape.color.optional(),
    position: taskStatusWritableShape.position.optional(),
    isDefault: taskStatusWritableShape.isDefault.optional(),
  })
  .strict();
export type CreateTaskStatusInput = z.input<typeof createTaskStatusInputSchema>;

/** PATCH /api/v1/task-statuses/:id (perm `projects:write`) → TaskStatus. Merge patch. */
export const updateTaskStatusInputSchema = z.object(taskStatusWritableShape).partial().strict();
export type UpdateTaskStatusInput = z.input<typeof updateTaskStatusInputSchema>;

/** POST /api/v1/task-statuses/reorder (perm `projects:write`) → the full list in the new order. */
export const reorderTaskStatusesInputSchema = z.object({ ids: z.array(uuidSchema).min(1).max(50) }).strict();
export type ReorderTaskStatusesInput = z.input<typeof reorderTaskStatusesInputSchema>;

/** A checklist line as the client sends it. `done` defaults to false. */
export const checklistItemInputSchema = z
  .object({ id: z.string().trim().max(40).optional(), text: z.string().trim().min(1).max(500), done: z.boolean().optional() })
  .strict();
export type ChecklistItemInput = z.input<typeof checklistItemInputSchema>;

const taskWritableShape = {
  title: z.string().trim().min(1).max(300),
  description: blankToNull(z.string().max(20000)),
  projectId: uuidSchema.nullable(),
  parentTaskId: uuidSchema.nullable(),
  statusId: uuidSchema,
  assigneeId: uuidSchema.nullable(),
  watcherIds: z.array(uuidSchema).max(50),
  startDate: dateOnlySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  dueTime: hhmmSchema.nullable(),
  priority: taskPrioritySchema,
  estimateMinutes: z.number().int().min(0).max(100000).nullable(),
  deliverableType: blankToNull(z.string().max(80)),
  checklist: z.array(checklistItemInputSchema).max(100),
  position: z.number().int().min(0),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/tasks (perm `tasks:write`) → 201 Task.
 * A `parentTaskId` that is itself a subtask → 422 (one level only, US-3.2).
 */
export const createTaskInputSchema = z
  .object({
    ...taskWritableShape,
    description: taskWritableShape.description.optional(),
    projectId: taskWritableShape.projectId.optional(),
    parentTaskId: taskWritableShape.parentTaskId.optional(),
    statusId: taskWritableShape.statusId.optional(),
    assigneeId: taskWritableShape.assigneeId.optional(),
    watcherIds: taskWritableShape.watcherIds.optional(),
    startDate: taskWritableShape.startDate.optional(),
    dueDate: taskWritableShape.dueDate.optional(),
    dueTime: taskWritableShape.dueTime.optional(),
    priority: taskWritableShape.priority.optional(),
    estimateMinutes: taskWritableShape.estimateMinutes.optional(),
    deliverableType: taskWritableShape.deliverableType.optional(),
    checklist: taskWritableShape.checklist.optional(),
    position: taskWritableShape.position.optional(),
    tags: taskWritableShape.tags.optional(),
    customFields: taskWritableShape.customFields.optional(),
  })
  .strict();
export type CreateTaskInput = z.input<typeof createTaskInputSchema>;
export type CreateTaskData = z.output<typeof createTaskInputSchema>;

/**
 * PATCH /api/v1/tasks/:id (perm `tasks:write`) → Task. Merge patch; omitted keys
 * unchanged, `null` clears. A stale `version` → 409 CONFLICT.
 */
export const updateTaskInputSchema = z
  .object({ ...taskWritableShape, version: z.number().int().min(1) })
  .partial()
  .strict();
export type UpdateTaskInput = z.input<typeof updateTaskInputSchema>;
export type UpdateTaskData = z.output<typeof updateTaskInputSchema>;

/** POST /api/v1/tasks/:id/complete (perm `tasks:write`) → Task. Credits the retainer quota (US-3.5). */
export const completeTaskInputSchema = z
  .object({ statusId: uuidSchema.optional(), completedAt: timestampSchema.optional(), version: z.number().int().min(1).optional() })
  .strict();
export type CompleteTaskInput = z.input<typeof completeTaskInputSchema>;

/** POST /api/v1/tasks/:id/checklist (perm `tasks:write`) → Task. */
export const addChecklistItemInputSchema = z.object({ text: z.string().trim().min(1).max(500) }).strict();
export type AddChecklistItemInput = z.input<typeof addChecklistItemInputSchema>;

/** PATCH /api/v1/tasks/:id/checklist/:itemId (perm `tasks:write`) → Task. */
export const updateChecklistItemInputSchema = z
  .object({ text: z.string().trim().min(1).max(500).optional(), done: z.boolean().optional() })
  .strict();
export type UpdateChecklistItemInput = z.input<typeof updateChecklistItemInputSchema>;

export const checklistItemParamsSchema = z.object({ id: uuidSchema, itemId: z.string().min(1).max(40) });
export type ChecklistItemParams = z.infer<typeof checklistItemParamsSchema>;

/**
 * POST /api/v1/tasks/:id/dependencies (perm `tasks:write`) → 201 TaskDependency.
 * `:id` becomes blocked by `predecessorTaskId`. A cycle → 409 CONFLICT.
 */
export const addTaskDependencyInputSchema = z
  .object({ predecessorTaskId: uuidSchema, type: taskDependencyTypeSchema.default("blocks") })
  .strict();
export type AddTaskDependencyInput = z.input<typeof addTaskDependencyInputSchema>;

/** POST /api/v1/tasks/:id/comments (perm `messages:write`) → 201 Activity. Mentions notify. */
export const createTaskCommentInputSchema = z
  .object({ body: z.string().trim().min(1).max(20000), mentions: z.array(uuidSchema).max(50).optional() })
  .strict();
export type CreateTaskCommentInput = z.input<typeof createTaskCommentInputSchema>;

// -- bulk operations (US-3.7) ------------------------------------------------

const bulkTaskIdsShape = { taskIds: z.array(uuidSchema).min(1).max(200) };

/** POST /api/v1/tasks/bulk/status (perm `tasks:write`) → BulkTaskResult. */
export const bulkTaskStatusInputSchema = z.object({ ...bulkTaskIdsShape, statusId: uuidSchema }).strict();
export type BulkTaskStatusInput = z.input<typeof bulkTaskStatusInputSchema>;

/** POST /api/v1/tasks/bulk/assignee (perm `tasks:write`) → BulkTaskResult. `null` unassigns. */
export const bulkTaskAssigneeInputSchema = z.object({ ...bulkTaskIdsShape, assigneeId: uuidSchema.nullable() }).strict();
export type BulkTaskAssigneeInput = z.input<typeof bulkTaskAssigneeInputSchema>;

/** POST /api/v1/tasks/bulk/shift-dates (perm `tasks:write`) → BulkTaskResult. Negative pulls earlier. */
export const bulkTaskShiftDatesInputSchema = z.object({ ...bulkTaskIdsShape, days: z.number().int().min(-3650).max(3650) }).strict();
export type BulkTaskShiftDatesInput = z.input<typeof bulkTaskShiftDatesInputSchema>;

/**
 * POST /api/v1/tasks/bulk/move (perm `tasks:write`) → BulkTaskResult.
 * Moving work to a project of a DIFFERENT client needs `confirmClientChange: true`
 * — otherwise 409 CONFLICT with the two client names in `details` (US-3.7).
 */
export const bulkTaskMoveInputSchema = z
  .object({ ...bulkTaskIdsShape, projectId: uuidSchema.nullable(), confirmClientChange: z.boolean().default(false) })
  .strict();
export type BulkTaskMoveInput = z.input<typeof bulkTaskMoveInputSchema>;

/** POST /api/v1/tasks/bulk/delete (perm `tasks:write`) → BulkTaskResult. Soft, 30-day trash. */
export const bulkTaskDeleteInputSchema = z.object(bulkTaskIdsShape).strict();
export type BulkTaskDeleteInput = z.input<typeof bulkTaskDeleteInputSchema>;

// -- recurring rules (US-3.4) -----------------------------------------------

const recurrenceConfigInputSchema = z
  .object({
    interval: z.number().int().min(1).max(365).default(1),
    unit: recurrenceUnitSchema.default("day"),
    weekdays: z.array(weekdaySchema).max(7).default([]),
    dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
    lastWorkingDay: z.boolean().default(false),
  })
  .strict();

const taskTemplateInputSchema = z
  .object({
    title: z.string().trim().min(1).max(300),
    description: blankToNull(z.string().max(20000)).optional(),
    assigneeId: uuidSchema.nullable().optional(),
    statusId: uuidSchema.nullable().optional(),
    priority: taskPrioritySchema.optional(),
    estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    deliverableType: blankToNull(z.string().max(80)).optional(),
    checklist: z.array(z.string().trim().min(1).max(500)).max(100).optional(),
    watcherIds: z.array(uuidSchema).max(50).optional(),
    tags: tagsSchema.optional(),
    dueOffsetDays: z.number().int().min(0).max(365).optional(),
  })
  .strict();

const recurringRuleWritableShape = {
  name: z.string().trim().min(1).max(200),
  projectId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  frequency: recurrenceFrequencySchema,
  config: recurrenceConfigInputSchema,
  generationMode: recurrenceGenerationModeSchema,
  template: taskTemplateInputSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema.nullable(),
  isActive: z.boolean(),
};

/** POST /api/v1/recurring-task-rules (perm `tasks:write`) → 201 RecurringTaskRule. */
export const createRecurringRuleInputSchema = z
  .object({
    ...recurringRuleWritableShape,
    projectId: recurringRuleWritableShape.projectId.optional(),
    engagementId: recurringRuleWritableShape.engagementId.optional(),
    config: recurrenceConfigInputSchema.optional(),
    generationMode: recurringRuleWritableShape.generationMode.optional(),
    endDate: recurringRuleWritableShape.endDate.optional(),
    isActive: recurringRuleWritableShape.isActive.optional(),
  })
  .strict();
export type CreateRecurringRuleInput = z.input<typeof createRecurringRuleInputSchema>;
export type CreateRecurringRuleData = z.output<typeof createRecurringRuleInputSchema>;

/** PATCH /api/v1/recurring-task-rules/:id (perm `tasks:write`) → RecurringTaskRule. Merge patch. */
export const updateRecurringRuleInputSchema = z
  .object({
    name: recurringRuleWritableShape.name,
    projectId: recurringRuleWritableShape.projectId,
    engagementId: recurringRuleWritableShape.engagementId,
    frequency: recurringRuleWritableShape.frequency,
    config: recurrenceConfigInputSchema,
    generationMode: recurringRuleWritableShape.generationMode,
    template: taskTemplateInputSchema,
    startDate: recurringRuleWritableShape.startDate,
    endDate: recurringRuleWritableShape.endDate,
    isActive: recurringRuleWritableShape.isActive,
  })
  .partial()
  .strict();
export type UpdateRecurringRuleInput = z.input<typeof updateRecurringRuleInputSchema>;
export type UpdateRecurringRuleData = z.output<typeof updateRecurringRuleInputSchema>;

export const recurrencePreviewQuerySchema = z.object({ count: z.coerce.number().int().min(1).max(24).default(5) });
export type RecurrencePreviewQuery = z.infer<typeof recurrencePreviewQuerySchema>;

export const listRecurringRulesQuerySchema = listQuerySchema.extend({
  projectId: uuidSchema.optional(),
  active: queryBooleanSchema.optional(),
});
export type ListRecurringRulesQuery = z.infer<typeof listRecurringRulesQuerySchema>;

/** POST /api/v1/work-holidays (perm `projects:write`) → 201 WorkHoliday. Duplicate date → CONFLICT. */
export const createWorkHolidayInputSchema = z.object({ date: dateOnlySchema, name: z.string().trim().min(1).max(120) }).strict();
export type CreateWorkHolidayInput = z.input<typeof createWorkHolidayInputSchema>;

export const listWorkHolidaysQuerySchema = z.object({ from: dateOnlySchema.optional(), to: dateOnlySchema.optional() });
export type ListWorkHolidaysQuery = z.infer<typeof listWorkHolidaysQuerySchema>;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /api/v1/tasks (perm `projects:read`) — the rich filter set behind every view (US-3.3). */
export const listTasksQuerySchema = listQuerySchema.extend({
  projectId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  assigneeId: uuidSchema.optional(),
  statusId: uuidSchema.optional(),
  statusCategory: csvArraySchema(taskStatusCategorySchema, TASK_STATUS_CATEGORIES.length).optional(),
  priority: csvArraySchema(taskPrioritySchema, TASK_PRIORITIES.length).optional(),
  tags: csvArraySchema(z.string().trim().min(1).max(40), 10).optional(),
  deliverableType: z.string().trim().max(80).optional(),
  parentTaskId: uuidSchema.optional(),
  /** "YYYY-MM-DD" bounds on the due date, inclusive. */
  dueFrom: dateOnlySchema.optional(),
  dueTo: dateOnlySchema.optional(),
  overdue: queryBooleanSchema.optional(),
  unassigned: queryBooleanSchema.optional(),
  /** Only top-level tasks (the default for the list and board views). */
  topLevelOnly: queryBooleanSchema.optional(),
  /** Include the 30-day trash instead of live tasks. */
  trashed: queryBooleanSchema.optional(),
  recurringRuleId: uuidSchema.optional(),
  sort: z.enum(["dueDate", "priority", "title", "number", "position", "createdAt", "updatedAt"]).optional(),
});
export type ListTasksQuery = z.infer<typeof listTasksQuerySchema>;

/** GET /api/v1/tasks/grouped (perm `projects:read`) — the same filters, bucketed. */
export const groupedTasksQuerySchema = listTasksQuerySchema.extend({ groupBy: taskGroupBySchema.default("status") });
export type GroupedTasksQuery = z.infer<typeof groupedTasksQuerySchema>;

/** GET /api/v1/tasks/board (perm `projects:read`) — one column per status. */
export const taskBoardQuerySchema = listTasksQuerySchema.extend({ columnLimit: z.coerce.number().int().min(1).max(200).default(50) });
export type TaskBoardQuery = z.infer<typeof taskBoardQuerySchema>;

/** GET /api/v1/tasks/calendar (perm `projects:read`) — a due-date window, max 186 days. */
export const taskCalendarQuerySchema = listTasksQuerySchema.extend({ from: dateOnlySchema, to: dateOnlySchema });
export type TaskCalendarQuery = z.infer<typeof taskCalendarQuerySchema>;

/**
 * GET /api/v1/tasks/my-work (perm `projects:read`) — the caller's own work by
 * default; naming someone else needs `projects:read_all`.
 */
export const myWorkQuerySchema = z.object({
  userId: uuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  includeUnscheduled: queryBooleanSchema.optional(),
});
export type MyWorkQuery = z.infer<typeof myWorkQuerySchema>;
